import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

const REF_COOKIE = 'ref';
/** Min length after sanitize to accept from URL / cookie / localStorage (e.g. dev `test`). */
const MIN_REF_CAPTURE_LEN = 1;
const COOKIE_OPTS = { expires: 365, sameSite: 'lax' };

function sanitizeRef(value) {
  return String(value || '').replace(/[^a-zA-Z0-9]/g, '');
}

/** Cookie + localStorage; used by Credit / ref flows after `RefLinkCapture` saves from URL. */
export function getStoredReferralCode() {
  try {
    const fromCookie = Cookies.get(REF_COOKIE);
    if (fromCookie) {
      const c = sanitizeRef(fromCookie);
      if (c.length >= MIN_REF_CAPTURE_LEN) return c;
    }
    const fromLs = window.localStorage.getItem(REF_COOKIE);
    if (fromLs) {
      const c = sanitizeRef(fromLs);
      if (c.length >= MIN_REF_CAPTURE_LEN) return c;
    }
  } catch {
    /* ignore */
  }
  return '';
}

/** Persist referral code for `getUserRefData` / fuel-buy flows (cookie + localStorage). */
export function persistReferralCode(code) {
  const v = sanitizeRef(code);
  if (v.length < MIN_REF_CAPTURE_LEN) return;
  Cookies.set(REF_COOKIE, v, COOKIE_OPTS);
  try {
    window.localStorage.setItem(REF_COOKIE, v);
  } catch {
    /* private mode / quota */
  }
}

/**
 * Read referral code from URL (same patterns as prior app):
 * - `/?ref=BullionDefiADMIN`
 * - `/ref?=BullionDefiADMIN` (legacy: query is `?=value`)
 * - `/ref/BullionDefiADMIN`
 */
export function parseReferralCode(search, pathname) {
  const q = search.startsWith('?') ? search.slice(1) : search;
  try {
    const params = new URLSearchParams(q);
    const fromParam = params.get('ref');
    if (fromParam) {
      const c = sanitizeRef(fromParam);
      if (c.length >= MIN_REF_CAPTURE_LEN) return c;
    }
  } catch {
    /* ignore */
  }

  const legacyEquals = search.match(/^\?=([^&]+)/);
  if (legacyEquals?.[1]) {
    const c = sanitizeRef(legacyEquals[1]);
    if (c.length >= MIN_REF_CAPTURE_LEN) return c;
  }

  const pathMatch = pathname.match(/^\/ref\/([^/]+)$/i);
  if (pathMatch?.[1]) {
    const c = sanitizeRef(decodeURIComponent(pathMatch[1]));
    if (c.length >= MIN_REF_CAPTURE_LEN) return c;
  }

  return null;
}

/**
 * If the URL carries a referral code, saves it with {@link persistReferralCode} and removes referral query params.
 * Use on the first landing page (`/`) and on inner routes that may include `?ref=`.
 *
 * @returns {boolean} true when a code was found and persisted
 */
export function captureReferralQueryOnLanding(pathname, search, navigate) {
  const code = parseReferralCode(search, pathname);
  if (!code) return false;
  persistReferralCode(code);
  const sp = new URLSearchParams(search.startsWith('?') ? search.slice(1) : '');
  sp.delete('ref');
  let newSearch = sp.toString() ? `?${sp.toString()}` : '';
  if (/^\?=([^&]+)/.test(search)) {
    newSearch = '';
  }
  navigate({ pathname, search: newSearch }, { replace: true });
  return true;
}

/**
 * `/ref` short links only. Query capture on `/` runs from {@link Home}.
 * Other routes: `?ref=` handled here so deep links still work.
 */
export default function RefLinkCapture() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { pathname, search } = location;

    if (pathname === '/ref' || pathname.toLowerCase().startsWith('/ref/')) {
      const code = parseReferralCode(search, pathname);
      if (code) {
        persistReferralCode(code);
      }
      navigate('/', { replace: true });
      return;
    }

    if (pathname === '/') {
      return;
    }

    captureReferralQueryOnLanding(pathname, search, navigate);
  }, [location.pathname, location.search, navigate]);

  return null;
}
